import { InfiniteScrollCarousel } from "@/components/ui/InfiniteScrollCarousel";
import { SelectableCard } from "@/components/ui/SelectableCard";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useExpertsData } from "@/data/use-experts-data";
import { useKitFoldersData } from "@/data/use-kit-folders-data";
import { useKitsData } from "@/data/use-kits-data";
import { useSkillsData } from "@/data/use-skills-data";
import { useKitTemplatesData } from "@/data/use-templates-data";
import { useGroupSlug } from "@/hooks/use-group-slug";
import { useGroupUuid } from "@/hooks/use-group-uuid";
import { cn } from "@/lib/utils";
import { Kit } from "@/types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { useSharedGroupUuid } from "../../hooks/use-shared-group-uuid";
import {
  GetKitDto,
  groupKitDocumentControllerCreateDocument,
} from "../../lib/gen-api";
import { LoadingOverlay } from "../LoadingOverlay/LoadingOverlay";
import { ExpertDisplayName } from "../display/ExpertDisplayName";
import {
  FolderDisplayName,
  SkillDisplayName,
  TemplateDisplayName,
} from "../display/display-name/artifacts";
import { getDefaultKitIcon } from "../display/display-name/defaults";
import { TruncationWithTooltip } from "../text/TruncationWithTooltip";
import { Image } from "../ui/Image";
import { Button } from "../ui/button";
import { Column } from "../ui/flex";
import { Input } from "../ui/input";

export function ProjectPresets() {
  const groupUuid = useGroupUuid();
  const sharedGroupUuid = useSharedGroupUuid();
  const [selectedPreset, setSelectedPreset] = useState<Kit | null>(null);

  const { kits } = useKitsData(groupUuid, {
    type: "project_preset",
    isPublished: true,
  });

  const groupPresets = kits.filter((k) => k.group_uuid !== sharedGroupUuid);

  const defaultPresets = kits.filter((k) => k.group_uuid === sharedGroupUuid);

  if (groupPresets.length === 0 && defaultPresets.length === 0) {
    return null;
  }

  return (
    <>
      <div className="flex w-full flex-row gap-8">
        {groupPresets.length > 0 && (
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            <h2 className="text-accent text-md font-semibold text-nowrap">
              Workspace Workbook Templates
            </h2>
            <InfiniteScrollCarousel resetKey={groupPresets.length}>
              <ul className="flex flex-row flex-nowrap gap-2 px-1 py-0.5">
                {groupPresets.map((preset) => (
                  <Preset
                    key={preset.uuid}
                    preset={preset}
                    onClick={() => setSelectedPreset(preset)}
                  />
                ))}
              </ul>
            </InfiniteScrollCarousel>
          </div>
        )}
        {defaultPresets.length > 0 && (
          <div className="flex min-w-0 flex-1 flex-col gap-4">
            <h2 className="text-md font-semibold text-nowrap">
              Default Workbook Templates
            </h2>
            <InfiniteScrollCarousel resetKey={defaultPresets.length}>
              <ul className="flex flex-row flex-nowrap gap-2 py-0.5">
                {defaultPresets.map((preset) => (
                  <Preset
                    key={preset.uuid}
                    preset={preset}
                    onClick={() => setSelectedPreset(preset)}
                  />
                ))}
              </ul>
            </InfiniteScrollCarousel>
          </div>
        )}
      </div>

      {selectedPreset && (
        <PresetDialog
          preset={selectedPreset}
          onOpenChange={() => setSelectedPreset(null)}
        />
      )}
    </>
  );
}

const Preset = ({ preset, onClick }: { preset: Kit; onClick: () => void }) => {
  return (
    <SelectableCard onClick={onClick}>
      <div className="bg-background-3 flex h-16 w-16 items-center justify-center overflow-hidden rounded-full">
        {preset.cover_image ? (
          <Image
            src={preset.cover_image}
            className="h-full w-full object-cover"
          />
        ) : (
          <FontAwesomeIcon
            icon={getDefaultKitIcon({ type: "project_preset" })}
            className="text-foreground-5 text-3xl"
          />
        )}
      </div>
      <TruncationWithTooltip className="line-clamp-3 w-full text-center text-sm font-semibold">
        {preset.name}
      </TruncationWithTooltip>
      <TruncationWithTooltip
        className={cn("text-foreground-8 line-clamp-3 text-center text-xs")}
      >
        {preset.description}
      </TruncationWithTooltip>
    </SelectableCard>
  );
};

function PresetDialog({
  preset,
  onOpenChange,
}: {
  preset: GetKitDto;
  onOpenChange: (open: boolean) => void;
}) {
  const groupUuid = useGroupUuid();
  const groupSlug = useGroupSlug();
  const navigate = useNavigate();
  const { createKit } = useKitsData(groupUuid, {
    type: "project",
  });

  const { templates } = useKitTemplatesData(groupUuid, preset.uuid);
  const { experts } = useExpertsData(groupUuid, preset.uuid);
  const { folders } = useKitFoldersData(groupUuid, preset.uuid);
  const { skills } = useSkillsData(groupUuid, preset.uuid);
  const [loading, setLoading] = useState(false);

  const [name, setName] = useState("Untitled Workbook");

  const handleSubmit = async () => {
    if (!groupUuid) return;
    setLoading(true);

    const kit = await createKit({
      name,
      presetKitUuid: preset.uuid,
      type: "project",
    });
    if (!kit) {
      toast.error("Failed to create workbook.");
      setLoading(false);
      return;
    }

    const documents = await Promise.all(
      templates.map((template) =>
        groupKitDocumentControllerCreateDocument({
          path: {
            groupUuid,
            kitUuid: kit.uuid,
          },
          body: {
            name: template.name,
            templateUuid: template.uuid,
          },
        }),
      ),
    );

    const firstDocument = documents.length > 0 ? documents[0] : null;

    if (firstDocument?.data) {
      navigate({
        to: "/w/$groupSlug/k/$kitUuid/document/$documentUuid",
        params: {
          groupSlug,
          kitUuid: kit.uuid,
          documentUuid: firstDocument.data.uuid,
        },
      });
    }

    navigate({
      to: "/w/$groupSlug/k/$kitUuid",
      params: { groupSlug, kitUuid: kit.uuid },
    });
  };

  return (
    <>
      <Dialog open={true} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Workbook</DialogTitle>
            <DialogDescription>
              Create a new workbook from {preset.name}.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Workbook name"
            disabled={loading}
            className="mb-2"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              }
            }}
          />
          <Column className="max-h-[50vh] gap-4 overflow-y-auto px-2">
            <h3 className="text-base font-semibold">Document Templates</h3>
            {templates.length === 0 ? (
              <div className="text-foreground-5 text-sm">
                No document templates
              </div>
            ) : (
              <div className="flex flex-row flex-wrap gap-2 text-sm">
                {templates.map((template) => (
                  <TemplateDisplayName
                    key={template.uuid}
                    template={template}
                  />
                ))}
              </div>
            )}

            <h3 className="text-base font-semibold">Experts</h3>
            {experts.length === 0 ? (
              <div className="text-foreground-5 text-sm">No experts</div>
            ) : (
              <div className="flex flex-row flex-wrap gap-2 text-sm">
                {experts.map((expert) => (
                  <ExpertDisplayName key={expert.uuid} expert={expert} />
                ))}
              </div>
            )}

            <h3 className="text-base font-semibold">Folders</h3>
            {folders.length === 0 ? (
              <div className="text-foreground-5 text-sm">No folders</div>
            ) : (
              <div className="flex flex-row flex-wrap gap-2 text-sm">
                {folders.map((folder) => (
                  <FolderDisplayName key={folder.uuid} folder={folder} />
                ))}
              </div>
            )}

            <h3 className="text-base font-semibold">Skills</h3>
            {skills.length === 0 ? (
              <div className="text-foreground-5 text-sm">No skills</div>
            ) : (
              <div className="flex flex-row flex-wrap gap-2 text-sm">
                {skills.map((skill) => (
                  <SkillDisplayName key={skill.uuid} skill={skill} />
                ))}
              </div>
            )}
          </Column>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSubmit}
              disabled={loading || !name.trim()}
            >
              {loading ? "Creating workbook..." : "Create workbook"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LoadingOverlay
        loading={loading}
        title={"Creating workbook"}
        description={"Please wait while your workbook is being created."}
      />
    </>
  );
}
